import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { portalFetch } from '../lib/supabase';

function Card({ children, style }) {
  return <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, ...style }}>{children}</div>;
}

function Toggle({ enabled, onChange, disabled }) {
  return (
    <div
      onClick={() => !disabled && onChange(!enabled)}
      style={{
        width: 44, height: 24, borderRadius: 12, cursor: disabled ? 'default' : 'pointer',
        background: enabled ? 'var(--orange)' : '#D1D5DB',
        position: 'relative', transition: 'background 0.2s', flexShrink: 0,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <div style={{
        position: 'absolute', top: 3, left: enabled ? 23 : 3,
        width: 18, height: 18, borderRadius: '50%', background: 'white',
        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
  );
}

const TRIGGERS = [
  {
    key: 'win_back',
    icon: '💌',
    title: 'Win-Back',
    description: 'Automatically re-engage customers who haven\'t ordered in 60 days.',
    audience: 'Sent to: Customer',
    stat: 'Recovers 5–15% of lapsed customers (industry benchmark)',
    fields: [
      { key: 'win_back_enabled', type: 'toggle', label: 'Enable' },
      { key: 'win_back_days', type: 'number', label: 'Days since last order', min: 14, max: 180 },
      { key: 'win_back_subject', type: 'text', label: 'Email subject' },
      { key: 'win_back_body', type: 'textarea', label: 'Message body' },
    ],
  },
  {
    key: 'post_first_order',
    icon: '🎉',
    title: 'Post First Order',
    description: 'Thank new customers 24 hours after their first order and nudge them to leave a review.',
    audience: 'Sent to: Customer',
    stat: 'Drives review volume and early loyalty',
    fields: [
      { key: 'post_first_order_enabled', type: 'toggle', label: 'Enable' },
      { key: 'post_first_order_subject', type: 'text', label: 'Email subject' },
      { key: 'post_first_order_body', type: 'textarea', label: 'Message body' },
    ],
  },
  {
    key: 'milestone',
    icon: '⭐',
    title: '5th Order Milestone',
    description: 'Celebrate when a customer reaches their 5th order — builds emotional loyalty.',
    audience: 'Sent to: Customer',
    stat: 'Turns repeat buyers into regulars',
    fields: [
      { key: 'milestone_enabled', type: 'toggle', label: 'Enable' },
      { key: 'milestone_count', type: 'number', label: 'Order count milestone', min: 3, max: 20 },
      { key: 'milestone_subject', type: 'text', label: 'Email subject' },
      { key: 'milestone_body', type: 'textarea', label: 'Message body' },
    ],
  },
  {
    key: 'review_5star',
    icon: '🌟',
    title: '5-Star Review Thank You',
    description: 'Automatically thank customers who leave a 5-star review.',
    audience: 'Sent to: Customer',
    stat: 'Deepens loyalty after a positive experience',
    fields: [
      { key: 'review_5star_enabled', type: 'toggle', label: 'Enable' },
      { key: 'review_5star_subject', type: 'text', label: 'Email subject' },
      { key: 'review_5star_body', type: 'textarea', label: 'Message body' },
    ],
  },
  {
    key: 'review_low',
    icon: '⚠️',
    title: 'Low Review Alert',
    description: 'Alert you immediately when a customer leaves a low rating so you can respond before it escalates.',
    audience: 'Sent to: You (restaurant owner)',
    stat: 'Early intervention prevents reputation damage',
    fields: [
      { key: 'review_low_enabled', type: 'toggle', label: 'Enable' },
      { key: 'review_low_threshold', type: 'number', label: 'Alert when rating is below', min: 2, max: 4 },
    ],
  },
  {
    key: 'loyalty_almost_there',
    icon: '⭐',
    title: 'Loyalty — Almost There',
    description: 'Automatically nudge customers when they reach $8 cashback — just $2 away from their $10 reward. Fires once per reward cycle.',
    audience: 'Sent to: Customer',
    stat: 'Converts near-reward customers into repeat visitors',
    fields: [
      { key: 'loyalty_almost_there_enabled', type: 'toggle', label: 'Enable' },
    ],
    badge: 'Loyalty',
    badgeColor: '#065F46',
    badgeBg: '#D1FAE5',
    isReadOnly: true,
  },
];

const VARIABLE_HINT = 'Available variables: {customer_name}, {restaurant_name}';

const TRIGGER_STAT_KEYS = {
  win_back: 'win_back',
  post_first_order: 'post_first_order',
  milestone: 'milestone_5',
  review_5star: 'review_5star',
  review_low: 'review_low',
  loyalty_almost_there: 'loyalty_almost_there',
};

export default function Campaigns() {
  const { restaurant } = useAuth();
  const [settings, setSettings] = useState(null);
  const [summary, setSummary]   = useState({});
  const [logs, setLogs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [tab, setTab]           = useState('triggers'); // 'triggers' | 'logs'
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    if (!restaurant?.id) return;
    setLoading(true);
    const data = await portalFetch('/campaigns');
    if (data) {
      setSettings(data.settings || {});
      setSummary(data.summary || {});
    }
    setLoading(false);
  }, [restaurant?.id]);

  useEffect(() => { load(); }, [load]);

  const loadLogs = useCallback(async () => {
    const data = await portalFetch('/campaigns?view=logs');
    if (data) setLogs(data.logs || []);
  }, []);

  useEffect(() => {
    if (tab === 'logs') loadLogs();
  }, [tab, loadLogs]);

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    await portalFetch('/campaigns', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(settings),
    }());
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';

  const triggerLabel = {
    win_back: 'Win-Back',
    post_first_order: 'Post First Order',
    milestone_5: 'Milestone',
    review_5star: '5-Star Thank You',
    review_low: 'Low Review Alert',
  };

  const totalSent = Object.values(summary).reduce((s, v) => s + v, 0);

  if (loading) return (
    <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--muted)' }}>
      Loading campaign settings...
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.8rem', fontWeight: 900, margin: 0 }}>Campaigns</h1>
          <p style={{ color: 'var(--muted)', fontSize: '0.875rem', marginTop: 4 }}>
            Automated email campaigns — fire-and-forget. Enable the triggers you want and TableChat handles the rest.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {saved && <span style={{ color: 'var(--green-tx)', fontSize: '0.875rem', fontWeight: 600 }}>✓ Saved</span>}
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ background: 'var(--orange)', color: 'white', border: 'none', borderRadius: 8, padding: '0.65rem 1.4rem', fontWeight: 700, fontSize: '0.875rem', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1 }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' }}>
        {TRIGGERS.map(t => (
          <Card key={t.key} style={{ padding: '1rem 1.2rem' }}>
            <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>{t.title}</div>
            <div style={{ fontFamily: 'Fraunces, serif', fontSize: '1.5rem', fontWeight: 900, color: 'var(--orange)' }}>
              {summary[TRIGGER_STAT_KEYS[t.key]] || 0}
            </div>
            <div style={{ fontSize: '0.7rem', color: 'var(--muted)', marginTop: 2 }}>emails sent</div>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {[['triggers', 'Triggers'], ['logs', 'Send History']].map(([val, label]) => (
          <button key={val} onClick={() => setTab(val)} style={{
            padding: '0.5rem 1.1rem', borderRadius: 8, fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer',
            background: tab === val ? 'var(--orange)' : 'white',
            color: tab === val ? 'white' : 'var(--mid)',
            border: tab === val ? '1px solid var(--orange)' : '1px solid var(--border)',
          }}>{label}</button>
        ))}
      </div>

      {/* Triggers tab */}
      {tab === 'triggers' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {/* Compliance notice */}
          <div style={{ background: '#FFF8E7', border: '1px solid #F59E0B', borderRadius: 10, padding: '0.9rem 1.2rem', fontSize: '0.83rem', color: '#92400E' }}>
            <strong>📋 Compliance:</strong> Emails are only sent to customers who opted in to marketing communications at checkout.
            Customers who did not opt in will never receive campaign emails.
          </div>

          {TRIGGERS.map(trigger => {
            const isExpanded = expanded === trigger.key;
            const isEnabled  = settings?.[`${trigger.key}_enabled`] !== false;

            return (
              <Card key={trigger.key} style={{ overflow: 'hidden' }}>
                {/* Trigger header row */}
                <div
                  onClick={() => setExpanded(isExpanded ? null : trigger.key)}
                  style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1.1rem 1.4rem', cursor: 'pointer', userSelect: 'none' }}
                >
                  <span style={{ fontSize: '1.5rem' }}>{trigger.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>{trigger.title}</span>
                      <span style={{
                        fontSize: '0.68rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: 10,
                        background: isEnabled ? 'var(--green-bg)' : 'var(--warm)',
                        color: isEnabled ? 'var(--green-tx)' : 'var(--muted)',
                      }}>{isEnabled ? 'Active' : 'Paused'}</span>
                      {trigger.badge && (
                        <span style={{
                          fontSize: '0.68rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: 10,
                          background: trigger.badgeBg, color: trigger.badgeColor,
                        }}>{trigger.badge}</span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: 2 }}>{trigger.description}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <Toggle
                      enabled={isEnabled}
                      onChange={val => handleChange(`${trigger.key}_enabled`, val)}
                      disabled={false}
                    />
                    <span style={{ color: 'var(--muted)', fontSize: '1rem', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'none' }}>▾</span>
                  </div>
                </div>

                {/* Expanded settings */}
                {isExpanded && (
                  <div style={{ borderTop: '1px solid var(--border)', padding: '1.2rem 1.4rem', background: 'var(--warm)' }}>
                    {/* Meta info */}
                    <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1rem', fontSize: '0.8rem', flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ color: 'var(--muted)' }}>👤 {trigger.audience}</span>
                      <span style={{ color: 'var(--muted)' }}>💡 {trigger.stat}</span>
                      {trigger.badge && (
                        <span style={{
                          fontSize: '0.7rem', fontWeight: 700, padding: '0.2rem 0.6rem',
                          borderRadius: 20, background: trigger.badgeBg, color: trigger.badgeColor,
                        }}>
                          {trigger.badge}
                        </span>
                      )}
                    </div>
                    {trigger.isReadOnly && (
                      <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#F0FDF4', border: '1px solid #6EE7B7', borderRadius: 8, fontSize: '0.8rem', color: '#065F46' }}>
                        ⭐ This campaign is powered by the loyalty engine. The message is sent automatically when a customer's cashback balance reaches $8 (80% of their $10 reward). Enable or pause it using the toggle above.
                      </div>
                    )}

                    {/* Fields */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {trigger.fields.filter(f => f.type !== 'toggle').map(field => (
                        <div key={field.key}>
                          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--mid)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {field.label}
                          </label>
                          {field.type === 'number' ? (
                            <input
                              type="number"
                              value={settings?.[field.key] ?? ''}
                              min={field.min}
                              max={field.max}
                              onChange={e => handleChange(field.key, parseInt(e.target.value))}
                              style={{ width: 100, padding: '0.6rem 0.8rem', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: '0.9rem', outline: 'none' }}
                            />
                          ) : field.type === 'textarea' ? (
                            <>
                              <textarea
                                value={settings?.[field.key] ?? ''}
                                rows={3}
                                onChange={e => handleChange(field.key, e.target.value)}
                                style={{ width: '100%', padding: '0.7rem 0.9rem', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: '0.875rem', outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'Arial, sans-serif' }}
                              />
                              <div style={{ fontSize: '0.72rem', color: 'var(--muted)', marginTop: 4 }}>{VARIABLE_HINT}</div>
                            </>
                          ) : (
                            <>
                              <input
                                type="text"
                                value={settings?.[field.key] ?? ''}
                                onChange={e => handleChange(field.key, e.target.value)}
                                style={{ width: '100%', padding: '0.65rem 0.9rem', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' }}
                              />
                              <div style={{ fontSize: '0.72rem', color: 'var(--muted)', marginTop: 4 }}>{VARIABLE_HINT}</div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Logs tab */}
      {tab === 'logs' && (
        <Card>
          {logs.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem', opacity: 0.4 }}>📭</div>
              <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>No campaign emails sent yet.</p>
            </div>
          ) : (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 2fr 120px 140px', gap: '1rem', padding: '0.75rem 1.4rem', borderBottom: '1px solid var(--border)', background: 'var(--warm)', borderRadius: '12px 12px 0 0' }}>
                {['Trigger', 'Recipient', 'Type', 'Sent'].map(h => (
                  <div key={h} style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</div>
                ))}
              </div>
              {logs.map((log, i) => (
                <div key={log.id} style={{ display: 'grid', gridTemplateColumns: '1.5fr 2fr 120px 140px', gap: '1rem', padding: '0.85rem 1.4rem', borderBottom: '1px solid var(--warm)', fontSize: '0.875rem', alignItems: 'center' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--cream)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'white'}
                >
                  <div style={{ fontWeight: 600 }}>{triggerLabel[log.trigger_type] || log.trigger_type}</div>
                  <div style={{ color: 'var(--mid)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.customer_email}</div>
                  <div>
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, background: 'var(--warm)', color: 'var(--orange)', padding: '0.15rem 0.5rem', borderRadius: 8, border: '1px solid var(--border)' }}>
                      Email
                    </span>
                  </div>
                  <div style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>{fmtDate(log.sent_at)}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
